import {Component, OnInit, OnDestroy, inject} from '@angular/core';
import {AuthenticationService} from '../_services/authentication.service';
import {NotificationService} from '../_services/gui/notification.service';
import {BranchesService} from '../_services/data/branches.service';
import {Observable, Subscription} from 'rxjs';
import {Branch} from '../_data/admin/branch';
import {first, map} from 'rxjs/operators';
import {AbstractControl} from '@angular/forms';
import {reformatDateTime} from '../_utils/lang';
import {exists} from '../_utils/operators';
import {ConsumerBusinessIndicator} from '../_constants/types';

/**
 * A base class to handle logic that's identical between the consumer and business order forms.
 * For example, they both have the same logic for generating an automatic reference,
 * and they both have to load the appropriate products when a branch is chosen in the drop down.
 */
@Component({
    selector: 'app-reports-services-base',
    template: '<div></div>'
})
export class ReportsServicesBaseComponent implements OnInit, OnDestroy {

  companyName: string;

  branches: Branch[];
  branchNames: string[] = [];
  
  protected authService = inject(AuthenticationService);
  protected notificationService = inject(NotificationService);
  protected branchesService = inject(BranchesService);

  protected consumerBusinessIndicator: ConsumerBusinessIndicator = 'C';

  private branchesSubscription: Subscription;
  private autoRefSubscription: Subscription;
  private branchObservable: Observable<Branch>;

  constructor() {
    this.authService.decodedAccessToken$
      .subscribe(jwt => this.companyName = jwt?.user?.company);
  }

  ngOnInit(): void {
    // Get logged-in user's company/branches
    this.branchesSubscription = this.loadUserBranches();
  }

  ngOnDestroy(): void {
    this.branchesSubscription?.unsubscribe();
    this.autoRefSubscription?.unsubscribe();
  }

  loadUserBranches(): Subscription {
    console.debug(`Filtering branches to ${this.consumerBusinessIndicator}...`);
    return this.authService.userBranches$.pipe(
      exists(),
      map((branches: Branch[]) =>
        branches
          .filter(b => b.productType === this.consumerBusinessIndicator)
          .sort((a, b) => a.branchName.localeCompare(b.branchName))
      )
    ).subscribe(branches => {
      this.branches = branches;
      this.branchNames = branches.map(b => b.branchName);
    });
  }

  /**
   * Monitor the selected branch from the drop down.
   * If the branch is set to have an automatic reference number generated, generate the reference (max 24 chars) and set the field to disabled.
   * Otherwise, clear the value and set to enabled.
   *
   * @param branchControl - The dropdown control to monitor
   * @param branches - A method that will return the list of branches
   * @param referenceControl - The text control for the reference number
   */
  autoRef(
    branchControl: AbstractControl,
    branches: () => Branch[],
    referenceControl: AbstractControl
  ): void {
    const allowedUsernameCharsLength = 11; // 24 max. 12 taken by date. 1 by the dash.
    this.autoRefSubscription = this.selectedBranch(branchControl, branches)
      .subscribe(branch => {
        if(branch && branch.autoRef === 'true') {
          console.debug('Auto assign reference and make field read-only');
          this.authService.decodedAccessToken$
            .pipe(first())
            .subscribe(user => {
              referenceControl.setValue(
                `${user.user.username.slice(0, allowedUsernameCharsLength)}-${reformatDateTime(
                  new Date(),
                  'YYMMDDHHmmss'
                )}`
              );
              referenceControl.disable({onlySelf: true});
            });
        } else {
          console.debug('Keep reference control field normal');
          referenceControl.setValue('');
          referenceControl.enable({onlySelf: true});
        }
      });
  }

  selectedBranch(
    branchControl: AbstractControl,
    branches: () => Branch[]
  ): Observable<Branch> {
    if(!this.branchObservable) {
      this.branchObservable = branchControl.valueChanges.pipe(
        map((value: string) => {
          console.debug(`Selected branch changed. ${value}`);
          return branches().find(b => b.branchName === value);
        })
      );
    }
    return this.branchObservable;
  }
}
